/**
 * Visual swimlane map for hcm-user-security-access-map query results.
 * Expects columns: username, access_layer, access_category, access_name, access_detail (any case).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Shield,
  KeyRound,
  Database,
  User,
  Users,
  Eye,
  ChevronDown,
  FileText,
  ChevronRight,
  Network,
  Sparkles,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { riskScoreTier } from './securityAccessMapApi';
import CollapsibleStrip from './CollapsibleStrip';

const PEOPLE_VISIBLE_DISPLAY_CAP = 50;

const LAYER_META = {
  ASSIGNED_ROLE: {
    title: 'Assigned roles',
    icon: Shield,
    bar: 'bg-sky-500',
    chip: 'border-sky-200 bg-sky-50 text-sky-900',
  },
  PRIVILEGE: {
    title: 'Privileges',
    icon: KeyRound,
    bar: 'bg-violet-500',
    chip: 'border-violet-200 bg-violet-50 text-violet-900',
  },
  FUNCTIONAL: {
    title: 'Functional access',
    icon: KeyRound,
    bar: 'bg-violet-500',
    chip: 'border-violet-200 bg-violet-50 text-violet-900',
  },
  DATA_SECURITY: {
    title: 'Data they can see',
    icon: Database,
    bar: 'bg-emerald-500',
    chip: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  },
  PERSON_SCOPE: {
    title: 'Access to other people',
    icon: Users,
    bar: 'bg-amber-500',
    chip: 'border-amber-200 bg-amber-50 text-amber-900',
  },
  PEOPLE_VISIBLE: {
    title: 'People they can see',
    icon: Eye,
    bar: 'bg-rose-500',
    chip: 'border-rose-200 bg-rose-50 text-rose-900',
  },
};

const LAYER_ORDER = [
  'ASSIGNED_ROLE',
  'PRIVILEGE',
  'DATA_SECURITY',
  'PERSON_SCOPE',
  'PEOPLE_VISIBLE',
];

function normKey(row, ...names) {
  if (!row || typeof row !== 'object') return '';
  const keys = Object.keys(row);
  for (const want of names) {
    const hit = keys.find((k) => k.toLowerCase() === want.toLowerCase());
    if (hit != null && row[hit] != null && String(row[hit]).trim() !== '') {
      return String(row[hit]).trim();
    }
  }
  return '';
}

function normalizeRows(rows) {
  return (rows || []).map((row) => ({
    username: normKey(row, 'username', 'user_name'),
    displayName: normKey(row, 'display_name', 'employee_name', 'full_name'),
    personNumber: normKey(row, 'person_number', 'employee_number'),
    layer: normKey(row, 'access_layer', 'layer').toUpperCase(),
    category: normKey(row, 'access_category', 'category'),
    name: normKey(row, 'access_name', 'role_name', 'name'),
    detail: normKey(row, 'access_detail', 'description', 'detail'),
  }));
}

export function isSecurityAccessMapResult(columns, rows) {
  const cols = (columns || []).map((c) => (typeof c === 'string' ? c : c.name || '').toLowerCase());
  if (cols.includes('access_layer') && cols.includes('access_name')) return true;
  const sample = rows?.[0];
  if (!sample) return false;
  return normKey(sample, 'access_layer') !== '' && normKey(sample, 'access_name') !== '';
}

export function sqlLooksLikeSecurityAccessMap(sql) {
  if (!sql || typeof sql !== 'string') return false;
  const s = sql.toLowerCase();
  return (
    s.includes('access_layer') &&
    (s.includes('access_name') || s.includes('access_category'))
  );
}

function userMatchesQuery(user, query) {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [user.displayName, user.personNumber, user.username].some(
    (v) => v && String(v).toLowerCase().includes(needle)
  );
}

function formatUserOptionLabel(user) {
  const name = user.displayName || user.username;
  const extras = [];
  if (user.displayName && user.username !== user.displayName) extras.push(user.username);
  if (user.personNumber) extras.push(`#${user.personNumber}`);
  return extras.length ? `${name} · ${extras.join(' · ')}` : name;
}

function buildUserIndex(normalized) {
  const byUsername = new Map();
  for (const r of normalized) {
    if (!r.username) continue;
    const existing = byUsername.get(r.username);
    if (!existing) {
      byUsername.set(r.username, {
        username: r.username,
        displayName: r.displayName,
        personNumber: r.personNumber,
      });
      continue;
    }
    if (!existing.displayName && r.displayName) existing.displayName = r.displayName;
    if (!existing.personNumber && r.personNumber) existing.personNumber = r.personNumber;
  }
  return [...byUsername.values()].sort((a, b) => {
    const aLabel = (a.displayName || a.username).toLowerCase();
    const bLabel = (b.displayName || b.username).toLowerCase();
    return aLabel.localeCompare(bLabel);
  });
}

function normalizeLayerId(layer) {
  let id = String(layer || '').toUpperCase();
  if (id === 'FUNCTIONAL') id = 'PRIVILEGE';
  if (id === 'SECURED_PERSONS') id = 'PEOPLE_VISIBLE';
  if (!LAYER_ORDER.includes(id)) id = 'ASSIGNED_ROLE';
  return id;
}

export function collapseViewAllPersonScope(items) {
  const list = items || [];
  const viewAll = list.filter((i) => i.category === 'VIEW_ALL_PERSONS');
  const rest = list.filter((i) => i.category !== 'VIEW_ALL_PERSONS');
  if (viewAll.length <= 1) return list;
  const profileNames = [...new Set(viewAll.map((i) => i.name).filter(Boolean))];
  const detailParts = profileNames.slice(0, 4);
  const detail =
    `${viewAll.length} profile(s) grant view all` +
    (detailParts.length ? ` — ${detailParts.join('; ')}` : '') +
    (profileNames.length > 4 ? '…' : '');
  return [
    ...rest,
    { category: 'VIEW_ALL_PERSONS', name: 'View All People', detail },
  ];
}

export function buildPeopleVisibleLaneItems(rows) {
  const list = rows || [];
  const viewAll = list.filter((i) => i.category === 'VIEW_ALL_PERSONS');
  if (viewAll.length > 0) {
    const profiles = [...new Set(viewAll.map((i) => i.detail || i.name).filter(Boolean))];
    return [
      {
        category: 'VIEW_ALL_PERSONS',
        name: 'All people (view all)',
        detail:
          profiles.length > 0
            ? `Via person security profile: ${profiles.slice(0, 3).join('; ')}${profiles.length > 3 ? '…' : ''}`
            : 'Tenant-wide person visibility from view-all security profile',
      },
    ];
  }
  return list.filter((i) => i.category === 'SECURED_PERSON');
}

export function buildAccessSummaryEnglish(username, layers, meta = {}) {
  const u = String(username || meta.username || '').trim() || 'this user';
  const label = meta.displayName ? `${meta.displayName} (${u})` : u;
  const sentences = [];

  const roles = (layers.ASSIGNED_ROLE || []).filter((r) => r.category !== 'NO_ROLES');
  if (roles.length) {
    const names = roles.map((r) => r.name).filter(Boolean);
    const sample = names.slice(0, 10).join(', ');
    sentences.push(
      `${label} has ${roles.length} assigned role(s) in Fusion, including ${sample}${names.length > 10 ? ', and others' : ''}.`
    );
  } else if ((layers.ASSIGNED_ROLE || []).some((r) => r.category === 'NO_ROLES')) {
    sentences.push(`${label} is an active Fusion user with no assigned roles in the map.`);
  } else {
    sentences.push(`${label} has no assigned-role rows in the loaded map.`);
  }

  const privs = layers.PRIVILEGE || [];
  if (privs.length) {
    sentences.push(`${privs.length} privilege bundle(s) (duty roles).`);
  }

  const data = layers.DATA_SECURITY || [];
  if (data.length) {
    const objects = [...new Set(data.map((r) => r.category).filter(Boolean))];
    sentences.push(
      `Data security: ${data.length} generated data role profile(s) covering ${objects.slice(0, 6).join(', ') || 'HR securing objects'}.`
    );
  }

  const personScope = layers.PERSON_SCOPE || [];
  const viewAllPolicy = personScope.some((p) => p.category === 'VIEW_ALL_PERSONS');
  const delegations = personScope.filter((p) => String(p.category || '').startsWith('DELEGATION'));
  if (viewAllPolicy) {
    sentences.push(
      'Person-scope policies include at least one “view all persons” security profile (see Access to other people).'
    );
  } else if (personScope.length) {
    sentences.push(
      `Person-scope rules: ${personScope.length} profile, delegation, or org-scope row(s) — these describe policy, not every worker name.`
    );
  }
  if (delegations.length) {
    sentences.push(`${delegations.length} active delegation row(s) affect proxy or granted access.`);
  }

  const peopleLane = buildPeopleVisibleLaneItems(layers.PEOPLE_VISIBLE || []);
  if (peopleLane.some((p) => p.category === 'VIEW_ALL_PERSONS')) {
    sentences.push('They can see all people in the tenant (view-all person security).');
  } else if (peopleLane.length) {
    const sample = peopleLane
      .slice(0, 5)
      .map((p) => (p.detail ? `#${p.name} ${p.detail}` : `#${p.name}`))
      .join('; ');
    sentences.push(
      `Named workers visible in person security cache: ${peopleLane.length} (sample: ${sample}${peopleLane.length > 5 ? '…' : ''}).`
    );
  } else if (personScope.length || data.length) {
    sentences.push(
      'Load extended person scope to see named workers; org or hierarchy scope may apply without listing every person.'
    );
  }

  return sentences.join(' ');
}

function buildLayersByUser(normalized, activeUser) {
  const filtered = normalized.filter((r) => r.username === activeUser);
  const layers = {};
  for (const id of LAYER_ORDER) layers[id] = [];

  for (const r of filtered) {
    if (r.category === 'NO_ROLES') continue;
    const layer = normalizeLayerId(r.layer);
    layers[layer].push(r);
  }

  layers.PERSON_SCOPE = collapseViewAllPersonScope(layers.PERSON_SCOPE);
  layers.PEOPLE_VISIBLE = buildPeopleVisibleLaneItems(layers.PEOPLE_VISIBLE);

  return layers;
}

/** Build nested role hierarchy from map rows (job → inherited → duty privileges). */
export function buildRoleHierarchyForest(normalized, username) {
  const rows = (normalized || []).filter((r) => r.username === username);
  const jobRoots = rows.filter(
    (r) =>
      r.layer === 'ASSIGNED_ROLE' &&
      ['JOB_ROLE', 'ABSTRACT_ROLE', 'DUTY_ROLE'].includes(String(r.category || '').toUpperCase())
  );
  const inheritedRoles = rows.filter(
    (r) =>
      r.layer === 'ASSIGNED_ROLE' &&
      String(r.category || '').toUpperCase().startsWith('INHERITED')
  );
  const hierarchyLayer = rows.filter((r) => r.layer === 'ROLE_HIERARCHY');
  const privileges = rows.filter((r) => r.layer === 'PRIVILEGE');

  const toNode = (r, kind) => ({
    id: `${kind}-${r.name}-${r.category}`,
    label: r.name,
    kind,
    category: r.category,
    detail: r.detail,
    children: [],
  });

  const forest = jobRoots.map((r) => toNode(r, 'job'));

  if (inheritedRoles.length || hierarchyLayer.length) {
    forest.push({
      id: '__inherited__',
      label: 'Inherited via role hierarchy (SRM)',
      kind: 'group',
      category: 'INHERITED',
      detail: 'Inherited via role hierarchy',
      children: [
        ...inheritedRoles.map((r) => toNode(r, 'inherited')),
        ...hierarchyLayer.map((r) => toNode(r, 'hierarchy')),
      ],
    });
  }

  if (privileges.length) {
    forest.push({
      id: '__privileges__',
      label: 'Duty roles & functional access',
      kind: 'group',
      category: 'PRIVILEGE',
      detail: 'Duty roles and functional access',
      children: privileges.map((r) => {
        const node = toNode(r, r.category === 'INHERITED_DUTY' ? 'inherited_duty' : 'duty');
        if (r.detail) {
          node.children.push({
            id: `${node.id}-fn`,
            label: r.detail.slice(0, 120),
            kind: 'functional',
            category: 'FUNCTIONAL',
            detail: r.detail,
            children: [],
          });
        }
        return node;
      }),
    });
  }

  return forest;
}

function HierarchyTreeNode({ node, depth = 0 }) {
  const [open, setOpen] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const indent = { paddingLeft: `${Math.min(depth, 6) * 12 + 8}px` };

  return (
    <li className="list-none">
      <div
        className="flex items-start gap-1 rounded py-0.5 hover:bg-slate-50"
        style={indent}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="mt-0.5 shrink-0 text-slate-400"
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="inline-block w-3.5 shrink-0" />
        )}
        <HierarchyNodeLabel node={node} />
      </div>
      {hasChildren && open ? (
        <ul>
          {node.children.map((child) => (
            <HierarchyTreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function HierarchyNodeLabel({ node }) {
  const kindChip =
    node.kind === 'job'
      ? 'bg-sky-100 text-sky-800'
      : node.kind === 'inherited_duty' || node.kind === 'inherited'
        ? 'bg-violet-100 text-violet-800'
        : node.kind === 'functional'
          ? 'bg-slate-100 text-slate-600'
          : 'bg-amber-100 text-amber-900';
  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-slate-800">{node.label}</span>
        <span className={`rounded px-1 py-0.5 text-[9px] font-medium uppercase ${kindChip}`}>
          {String(node.category || node.kind).replace(/_/g, ' ')}
        </span>
      </div>
      {node.detail && node.kind !== 'functional' ? (
        <p className="line-clamp-2 text-[10px] text-slate-500">{node.detail}</p>
      ) : null}
    </div>
  );
}

function RoleHierarchyPanel({ forest }) {
  if (!forest.length) {
    return (
      <p className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-500">
        No hierarchy nodes. Load a security map first.
      </p>
    );
  }
  return (
    <ul className="max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-white py-2">
      {forest.map((node) => (
        <HierarchyTreeNode key={node.id} node={node} depth={0} />
      ))}
    </ul>
  );
}

export function UserSearchCombobox({ users, value, onChange, accentStyles }) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = users.find((u) => u.username === value) || users[0];
  const filtered = useMemo(
    () => users.filter((u) => userMatchesQuery(u, query)),
    [users, query]
  );

  useEffect(() => {
    if (!selected) return;
    setQuery(formatUserOptionLabel(selected));
  }, [selected?.username]);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (user) => {
    onChange(user.username);
    setQuery(formatUserOptionLabel(user));
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative min-w-[14rem]">
        <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter' && filtered[0]) {
              e.preventDefault();
              pick(filtered[0]);
            }
          }}
          placeholder="Search name, person #, username…"
          className={`w-full rounded-md border border-slate-300 bg-white py-1 pl-2 pr-7 text-sm text-slate-800 focus:outline-none focus:ring-1 ${accentStyles.inputFocus}`}
          aria-label="Search user by display name, person number, or username"
          aria-expanded={open}
          aria-haspopup="listbox"
        />
        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>
      {open && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-xs text-slate-500">No matching users</li>
          ) : (
            filtered.map((u) => (
              <li key={u.username} role="option" aria-selected={u.username === value}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(u)}
                  className={`block w-full px-3 py-1.5 text-left ${accentStyles.optionHover} ${
                    u.username === value ? accentStyles.optionSelected : 'text-slate-800'
                  }`}
                >
                  <span className="block truncate">{formatUserOptionLabel(u)}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

export const ACCENT = {
  indigo: {
    inputFocus: 'focus:border-indigo-500 focus:ring-indigo-500',
    optionHover: 'hover:bg-indigo-50',
    optionSelected: 'bg-indigo-50 font-medium text-indigo-900',
  },
  cyan: {
    inputFocus: 'focus:border-cyan-500 focus:ring-cyan-500',
    optionHover: 'hover:bg-cyan-50',
    optionSelected: 'bg-cyan-50 font-medium text-cyan-900',
  },
};

function PeopleVisibleCards({ items, meta }) {
  const viewAll = items.some((i) => i.category === 'VIEW_ALL_PERSONS');
  const secured = items.filter((i) => i.category === 'SECURED_PERSON');
  const shown = secured.slice(0, PEOPLE_VISIBLE_DISPLAY_CAP);
  const extra = secured.length - shown.length;

  if (viewAll && items.length > 0) {
    const card = items[0];
    return (
      <div
        className={`rounded-md border px-2.5 py-2 ${meta.chip}`}
        title={card.detail || card.name}
      >
        <div className="text-[10px] font-medium uppercase tracking-wide opacity-70">
          view all persons
        </div>
        <div className="text-xs font-semibold leading-snug">{card.name}</div>
        {card.detail ? (
          <p className="mt-1 line-clamp-3 text-[10px] opacity-80">{card.detail}</p>
        ) : null}
      </div>
    );
  }

  if (shown.length === 0) {
    return (
      <p className="px-2 py-4 text-center text-[11px] text-slate-400">
        Load extended person scope for named workers from the materialized cache.
      </p>
    );
  }

  return (
    <>
      {shown.map((item, i) => (
        <div
          key={`${item.name}-${item.detail}-${i}`}
          className={`rounded-md border px-2.5 py-2 ${meta.chip}`}
          title={item.detail || item.name}
        >
          <div className="text-[10px] font-medium uppercase tracking-wide opacity-70">
            #{item.name}
          </div>
          <div className="text-xs font-semibold leading-snug">{item.detail || item.name}</div>
        </div>
      ))}
      {extra > 0 ? (
        <p className="px-2 py-1 text-center text-[10px] font-medium text-slate-500">
          and {extra.toLocaleString()} more
        </p>
      ) : null}
    </>
  );
}

function isFlaggedAccess(name, flaggedRoles) {
  if (!name || !Array.isArray(flaggedRoles) || !flaggedRoles.length) return false;
  const n = String(name).toLowerCase();
  return flaggedRoles.some((f) => {
    const flag = String(f || '').toLowerCase().trim();
    return flag && (n.includes(flag) || flag.includes(n));
  });
}

function riskBadgeClasses(score) {
  const tier = riskScoreTier(score);
  if (tier === 'low') return 'border-emerald-300 bg-emerald-50 text-emerald-900';
  if (tier === 'medium') return 'border-amber-300 bg-amber-50 text-amber-900';
  if (tier === 'high') return 'border-rose-300 bg-rose-50 text-rose-900';
  return 'border-slate-300 bg-slate-50 text-slate-800';
}

function LayerCards({ layerId, items, flaggedRoles = [] }) {
  const meta = LAYER_META[layerId];
  const showFlags = layerId === 'ASSIGNED_ROLE' || layerId === 'DATA_SECURITY';
  if (layerId === 'PEOPLE_VISIBLE') {
    return <PeopleVisibleCards items={items} meta={meta} />;
  }
  if (items.length === 0) {
    return <p className="px-2 py-4 text-center text-[11px] text-slate-400">None on file</p>;
  }
  return items.map((item, i) => (
    <div
      key={`${item.name}-${item.category}-${i}`}
      className={`relative rounded-md border px-2.5 py-2 ${meta.chip} ${
          showFlags && isFlaggedAccess(item.name, flaggedRoles)
            ? 'ring-2 ring-rose-400 ring-offset-1'
            : ''
        }`}
      title={item.detail || item.name}
    >
      {showFlags && isFlaggedAccess(item.name, flaggedRoles) ? (
        <span
          className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-white shadow"
          title="Flagged by AI security audit"
        >
          <AlertTriangle className="h-3 w-3" aria-hidden />
        </span>
      ) : null}
      <div className="text-[10px] font-medium uppercase tracking-wide opacity-70">
        {item.category.replace(/_/g, ' ')}
      </div>
      <div className="text-xs font-semibold leading-snug">{item.name}</div>
      {item.detail ? (
        <p className="mt-1 line-clamp-2 text-[10px] opacity-80">{item.detail}</p>
      ) : null}
    </div>
  ));
}

export default function UserSecurityAccessMap({
  rows,
  initialUsername = '',
  employeePickerUsers = null,
  selectedUsername = '',
  onEmployeeSelect,
  accent = 'indigo',
  onSecurityAudit = null,
  securityAuditLoading = false,
  auditResult = null,
  sodConflicts = [],
  sodLoading = false,
}) {
  const normalized = useMemo(() => normalizeRows(rows), [rows]);
  const mapUserIndex = useMemo(() => buildUserIndex(normalized), [normalized]);
  const pickerUsers = useMemo(() => {
    if (Array.isArray(employeePickerUsers) && employeePickerUsers.length > 0) {
      return employeePickerUsers;
    }
    return mapUserIndex;
  }, [employeePickerUsers, mapUserIndex]);

  const controlledPicker = Array.isArray(employeePickerUsers) && employeePickerUsers.length > 0;
  const [selectedUser, setSelectedUser] = useState('');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [hierarchyOpen, setHierarchyOpen] = useState(false);

  useEffect(() => {
    if (!initialUsername) return;
    const match = pickerUsers.some((u) => u.username === initialUsername);
    if (match) setSelectedUser(initialUsername);
  }, [initialUsername, pickerUsers]);

  useEffect(() => {
    if (!controlledPicker || !selectedUsername) return;
    if (pickerUsers.some((u) => u.username === selectedUsername)) {
      setSelectedUser(selectedUsername);
    }
  }, [controlledPicker, selectedUsername, pickerUsers]);

  const activeUser =
    (controlledPicker && selectedUsername) ||
    selectedUser ||
    pickerUsers[0]?.username ||
    '';
  const accentStyles = ACCENT[accent] || ACCENT.indigo;

  const byUser = useMemo(
    () => buildLayersByUser(normalized, activeUser),
    [normalized, activeUser]
  );

  const summaryText = useMemo(
    () =>
      buildAccessSummaryEnglish(activeUser, byUser, {
        displayName:
          pickerUsers.find((u) => u.username === activeUser)?.displayName ||
          mapUserIndex.find((u) => u.username === activeUser)?.displayName ||
          '',
      }),
    [activeUser, byUser, pickerUsers, mapUserIndex]
  );

  const roleHierarchyForest = useMemo(
    () => (activeUser ? buildRoleHierarchyForest(normalized, activeUser) : []),
    [normalized, activeUser]
  );

  const activeMeta =
    pickerUsers.find((u) => u.username === activeUser) ||
    mapUserIndex.find((u) => u.username === activeUser);
  const personNumber = activeMeta?.personNumber || '';
  const displayName = activeMeta?.displayName || '';
  const flaggedRoles = Array.isArray(auditResult?.flaggedRoles) ? auditResult.flaggedRoles : [];
  const auditRiskScore =
    auditResult?.riskScore != null && Number.isFinite(Number(auditResult.riskScore))
      ? Number(auditResult.riskScore)
      : null;

  if (!normalized.length && !(controlledPicker && pickerUsers.length > 0)) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
        No security map rows. Load the access map.
      </div>
    );
  }

  const sodList = Array.isArray(sodConflicts) ? sodConflicts : [];

  return (
    <div className="space-y-4">
      {sodList.length > 0
        ? sodList.map((c, i) => (
            <div
              key={`${c.ruleName}-${i}`}
              className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900"
              role="alert"
            >
              <span className="font-semibold">⚠️ SoD Conflict{c.riskLevel ? ` (${c.riskLevel})` : ''}:</span>{' '}
              {c.description || c.message}
              {c.matchedPrivileges?.length ? (
                <span className="mt-1 block text-xs text-rose-800/90">
                  Matched: {c.matchedPrivileges.join(', ')}
                </span>
              ) : null}
            </div>
          ))
        : null}
      {sodLoading ? (
        <p className="text-xs text-slate-500">Checking segregation of duties…</p>
      ) : null}
      <CollapsibleStrip
        storageKey="aiTool.userLookup.accessMapChrome"
        title="Employee"
        collapsedSummary={
          activeUser
            ? `${displayName || activeUser} · ${activeUser}${personNumber ? ` · ${personNumber}` : ''}`
            : pickerUsers.length > 0
              ? `${pickerUsers.length.toLocaleString()} directory accounts`
              : 'Select employee'
        }
        className="rounded-lg border border-slate-200 bg-gradient-to-r from-slate-50 to-white"
        headerClassName="flex flex-wrap items-center gap-2 border-b border-slate-200/80 bg-white/60 px-4 py-2"
        titleClassName="text-sm font-semibold text-slate-800"
        headerActions={
          normalized.length > 0 && activeUser ? (
            <>
              <button
                type="button"
                onClick={() => setSummaryOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <FileText className="h-3.5 w-3.5" />
                Summarize access
                {summaryOpen ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                type="button"
                onClick={() => setHierarchyOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <Network className="h-3.5 w-3.5" />
                {hierarchyOpen ? 'Hide' : 'Show'} role hierarchy
              </button>
              {typeof onSecurityAudit === 'function' ? (
                <button
                  type="button"
                  onClick={() => onSecurityAudit(activeUser)}
                  disabled={securityAuditLoading}
                  className="inline-flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-900 hover:bg-violet-100 disabled:opacity-50"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {securityAuditLoading ? 'Auditing…' : 'AI Security Audit'}
                </button>
              ) : null}
            </>
          ) : null
        }
      >
        <div className="flex flex-wrap items-center gap-3 px-4 py-3">
          {pickerUsers.length > 0 ? (
            <label className="flex flex-col gap-1 text-xs text-slate-600 sm:flex-row sm:items-center">
              <span className="shrink-0">Employee</span>
              <UserSearchCombobox
                users={pickerUsers}
                value={activeUser}
                onChange={(username) => {
                  setSelectedUser(username);
                  setSummaryOpen(false);
                  if (typeof onEmployeeSelect === 'function') onEmployeeSelect(username);
                }}
                accentStyles={accentStyles}
              />
            </label>
          ) : null}
          {activeUser ? (
            <div className="text-xs text-slate-600">
              {displayName ? (
                <span className="font-semibold text-slate-900">{displayName}</span>
              ) : null}
              <span className={`font-mono text-slate-800 ${displayName ? 'ml-2' : ''}`}>{activeUser}</span>
              {personNumber ? (
                <span className="ml-2 text-slate-500">· {personNumber}</span>
              ) : null}
            </div>
          ) : null}
        </div>
      </CollapsibleStrip>

      {summaryOpen && summaryText ? (
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 px-4 py-3 text-sm leading-relaxed text-slate-800">
          {summaryText}
        </div>
      ) : null}

      {auditRiskScore != null && auditResult?.reasoning ? (
        <div
          className={`rounded-lg border px-4 py-3 ${riskBadgeClasses(auditRiskScore)}`}
          role="alert"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">AI Security Audit</span>
            <span
              className={`rounded-full border px-2.5 py-0.5 text-xs font-bold ${riskBadgeClasses(auditRiskScore)}`}
            >
              Risk {auditRiskScore}
            </span>
            {flaggedRoles.length > 0 ? (
              <span className="text-[11px] opacity-90">
                {flaggedRoles.length} flagged role{flaggedRoles.length === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>
          <p className="mt-2 text-sm leading-relaxed">{auditResult.reasoning}</p>
        </div>
      ) : null}

      {hierarchyOpen && normalized.length > 0 ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-3">
          <RoleHierarchyPanel forest={roleHierarchyForest} />
        </div>
      ) : null}

      {normalized.length > 0 ? (
        <div className="relative">
          {securityAuditLoading ? (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-[1px]"
              aria-busy="true"
              aria-label="Running AI security audit"
            >
              <div className="flex items-center gap-2 rounded-lg border border-violet-200 bg-white px-4 py-3 text-sm font-medium text-violet-900 shadow-sm">
                <Loader2 className="h-5 w-5 animate-spin text-violet-600" />
                AI security audit…
              </div>
            </div>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {LAYER_ORDER.map((layerId) => {
            const meta = LAYER_META[layerId];
            const Icon = meta.icon;
            const items = byUser[layerId] || [];
            return (
              <section
                key={layerId}
                className="flex min-h-[12rem] flex-col rounded-lg border border-slate-200 bg-white shadow-sm"
              >
                <div className={`h-1 rounded-t-lg ${meta.bar}`} />
                <div className="border-b border-slate-100 px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-slate-500" />
                    <h3 className="text-sm font-semibold text-slate-800">{meta.title}</h3>
                    <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                      {items.length}
                    </span>
                  </div>
                </div>
                <div className="max-h-[28rem] flex-1 space-y-2 overflow-y-auto p-2">
                  <LayerCards layerId={layerId} items={items} flaggedRoles={flaggedRoles} />
                </div>
              </section>
            );
          })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
